/**
 * MetricSlider Component
 *
 * A reusable slider component for collecting 1-10 scale metrics with visual feedback.
 * Features gradient fill, Lucide icon indicators, descriptive labels, and mobile-friendly controls.
 *
 * Supports both Lucide icon components (preferred) and legacy emoji strings.
 *
 * @component
 * @example
 * ```tsx
 * <MetricSlider
 *   label="Soreness Level"
 *   icon={Dumbbell}
 *   min={1}
 *   max={10}
 *   value={soreness}
 *   onChange={setSoreness}
 *   minLabel="None"
 *   maxLabel="Extreme"
 *   helpText="How sore are you feeling?"
 * />
 * ```
 */

import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

interface MetricSliderProps {
  /** Main label for the metric (e.g., "Soreness Level") */
  label: string;

  /** Lucide icon component to display next to the label */
  icon?: LucideIcon;

  /** Legacy emoji string — ignored when `icon` is provided */
  emoji?: string;

  /** Minimum value on the scale */
  min: number;

  /** Maximum value on the scale */
  max: number;

  /** Current value */
  value: number;

  /** Callback when value changes */
  onChange: (value: number) => void;

  /** Label for minimum value (e.g., "None", "Very Low") */
  minLabel: string;

  /** Label for maximum value (e.g., "Extreme", "Very High") */
  maxLabel: string;

  /** Optional help text explaining the metric */
  helpText?: string;

  /** Invert color scale: green at high values, red at low (e.g., motivation) */
  invertColor?: boolean;
}

/**
 * Get descriptive label based on value position in range.
 * Divides range into thirds: lower third = minLabel, middle = "Moderate", upper = maxLabel
 */
function getDescriptiveLabel(value: number, min: number, max: number, minLabel: string, maxLabel: string): string {
  const range = max - min;
  const third = range / 3;

  if (value <= min + third) return minLabel;
  if (value <= min + (third * 2)) return 'Moderate';
  return maxLabel;
}

/** Returns a color based on value position in range.
 *  Normal: green → yellow → orange → red (high = bad, e.g. soreness).
 *  Inverted: red → orange → yellow → green (high = good, e.g. motivation). */
function valueColor(value: number, min: number, max: number, invert = false): string {
  const pct = invert ? 1 - (value - min) / (max - min) : (value - min) / (max - min);
  if (pct <= 0.3) return '#22c55e';
  if (pct <= 0.5) return '#eab308';
  if (pct <= 0.7) return '#f97316';
  return '#ef4444';
}

export const MetricSlider: React.FC<MetricSliderProps> = ({
  label,
  icon: Icon,
  emoji,
  min,
  max,
  value,
  onChange,
  minLabel,
  maxLabel,
  helpText,
  invertColor = false,
}) => {
  const [localValue, setLocalValue] = useState(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  };

  // Calculate percentage for gradient fill
  const percentage = ((localValue - min) / (max - min)) * 100;
  const color = valueColor(localValue, min, max, invertColor);

  // Get descriptive label for current value
  const descriptiveLabel = getDescriptiveLabel(localValue, min, max, minLabel, maxLabel);

  return (
    <div className="mb-6">
      {/* Label + value */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          {Icon ? (
            <Icon className="w-5 h-5 mr-2" style={{ color: 'var(--text-secondary)' }} />
          ) : emoji ? (
            <span className="text-2xl mr-2" role="img" aria-label={label}>
              {emoji}
            </span>
          ) : null}
          <label className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {label}
          </label>
        </div>
        <span className="text-sm font-bold tabular-nums" style={{ color }}>
          {localValue}/{max}
        </span>
      </div>

      {/* Help Text */}
      {helpText && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
          {helpText}
        </p>
      )}

      {/* Slider */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          value={localValue}
          onChange={handleChange}
          className="metric-slider w-full h-2 cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${color} 0%, ${color} ${percentage}%, var(--card-border, #374151) ${percentage}%, var(--card-border, #374151) 100%)`,
            '--slider-color': color,
          } as React.CSSProperties}
          aria-label={`${label} slider`}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={localValue}
          aria-valuetext={`${localValue} - ${descriptiveLabel}`}
        />

        {/* Min/Max Labels */}
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      </div>
    </div>
  );
};
